'use client'
import {
  ChevronRightIcon,
  ClipboardIcon,
  PlusIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { isHotkey } from 'is-hotkey'
import * as React from 'react'
import {
  TreeManager,
  TreeManagerMethods,
  TreeNode,
} from '@basehub/utils/tree/index'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { z } from 'zod'
import { Button } from './button'
import { Input } from './input'
import { cva } from 'class-variance-authority'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
  ContextMenuLabel,
  ContextMenuItem,
  ContextMenuGroup,
  ContextMenuSeparator,
} from './context-menu'
import { DndController } from '../../app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/dnd-provider'
import { mergeRefs } from '../../lib/merge-refs'
import {
  SimpleTooltip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip'
import {
  isChangeBlock,
  BlockChange,
  blockTypesRecord,
  ComponentBlock,
  DocumentBlock,
  ListBlock,
} from '@basehub/data/replicache/index'
import { editorDndControllers } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/document/dnd'
import { BlockIcon } from '~/components/block-icon'
import { AtLeast } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { type Block } from '@basehub/data/replicache/index'
import { commandKeyPressed } from '~/lib/command-key-pressed'
import { pasteBlocksFromClipboard } from '~/hooks-data/copy-paste'
import { useIsMemberOrHigher } from '~/hooks-data/use-user-role'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useTabsStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useRepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { CollapseIcon } from '~/components/icons/expand-collapse'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { RepoUndoManager } from '~/replicache/frontend/undo-redo/manager'

/* -------------------------------------------------------------------------------------------------
 * Tree View
 * -----------------------------------------------------------------------------------------------*/

const treeContext = React.createContext<
  | undefined
  | {
      // general
      treeId: string
      treeKey: string
      treeRef: React.RefObject<null | HTMLDivElement>
      treeManager: TreeManagerMethods<Block | BlockChange>
      getKey: (node: TreeNode<Block | BlockChange>) => string
      dragAndDrop: 'enabled' | 'disabled' | 'enabled-drop-after'
      allowDrop?: (_params: {
        keyDragged: string
        newParentKey: string
      }) => boolean
      isCollapsed: boolean
      setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>
      autoFocusPath: string[]
      autoFocusKey?: string | null
      onAutoFocusKeyFocus: () => void
      // create new child
      isCreatingChild: IsCreatingChild
      setIsCreatingChild: (isCreatingChild: IsCreatingChild) => void
      onCreateNewChild?: TreeProps['onCreateNewChild']
      // active
      activeKey: { label: string; id: string } | null
      onActiveKeyChange: (
        key: { label: string; id: string },
        opts: { doubleClick: boolean }
      ) => void
      // selection
      selectedKeys: Set<{ label: string; id: string }>
      onSelectKeys: (array: { label: string; id: string }[]) => void
      onSelectKeyToggle: ({ label, id }: { label: string; id: string }) => void
      onSelectKeyRange: (
        startKey: { label: string; id: string },
        endKey: { label: string; id: string }
      ) => void
      onUnselectAll: () => void
      // focus stuff
      onFocusPrevListItem: (item: HTMLButtonElement) => void
      onFocusNextListItem: (item: HTMLButtonElement) => void
      onFocusParent: (item: HTMLButtonElement) => void
      onFocusFirstChild: (item: HTMLButtonElement) => void
      // collapse all
      onCollapseAll: () => void
      addCallbackCollapseAllList: (key: string, cb: () => void) => void
    }
>(undefined)

const useTreeContext = () => {
  const ctx = React.useContext(treeContext)
  if (!ctx) throw new Error('treeContext not found')
  return ctx
}

type TreeProps = {
  children?: React.ReactNode
  treeKey: string
  treeManager: TreeManagerMethods<Block | BlockChange>
  getKey?: (node: TreeNode<Block | BlockChange>) => string
  activeKey: { label: string; id: string } | null
  onActiveKeyChange: (
    key: { label: string; id: string },
    opts: { doubleClick: boolean }
  ) => void
  selectedKeys: Set<{ label: string; id: string }>
  setSelectedKeys: React.Dispatch<
    React.SetStateAction<Set<{ label: string; id: string }>>
  >
  autoFocusKey?: string | null
  onAutoFocusKeyFocus?: () => void
  onCreateNewChild?: (
    parent: TreeNode<Block | BlockChange>,
    block: AtLeast<Block, 'title' | 'type'>
  ) => void | Promise<void>
  className?: string
} & (
  | { dragAndDrop: 'disabled' }
  | {
      dragAndDrop: 'enabled' | 'enabled-drop-after'
      onDragEnd: (params: {
        draggedItems: z.infer<typeof dndControllers.draggableDataSchema>
        dropTarget: z.infer<typeof dndControllers.droppableDataSchema>
      }) => void
      allowDrop?: (_params: {
        keyDragged: string
        newParentKey: string
      }) => boolean
    }
)

const Tree = React.forwardRef<HTMLDivElement, TreeProps>(
  (
    {
      children,
      treeKey,
      treeManager,
      getKey,
      activeKey,
      onActiveKeyChange,
      selectedKeys,
      setSelectedKeys: setSelectedKeys_DONT_USE_DIRECTLY,
      className,
      autoFocusKey,
      onAutoFocusKeyFocus,
      onCreateNewChild,
      ...rest
    },
    ref
  ) => {
    const treeRef = React.useRef<HTMLDivElement>(null)
    const [isCollapsed, setIsCollapsed] = React.useState(false)
    const [autoFocusPath, setAutoFocusPath] = React.useState<string[]>([])
    const treeId = React.useId()
    const [isCreatingChild, setIsCreatingChild] =
      React.useState<IsCreatingChild>(false)

    getKey = getKey ?? ((node) => node.key)

    // receive event (via useEffect)
    // do a bunch of logic to make sure its
    // forward to onDragEnd from props
    React.useEffect(() => {
      const treeEl = treeRef.current
      if (!treeEl || rest.dragAndDrop === 'disabled') return

      const handleDnd = (event: unknown) => {
        const dndResult = z
          .object({
            detail: z.object({
              draggedItems: dndControllers.draggableDataSchema,
              dropTarget: dndControllers.droppableDataSchema,
            }),
          })
          .parse(event).detail

        if (
          isValidDnd(
            treeManager,
            dndResult.draggedItems.keysDragged,
            dndResult.dropTarget.newParentKey,
            dndResult.dropTarget.intendedIndex,
            getKey,
            rest.dragAndDrop === 'enabled' ? rest.allowDrop : undefined
          )
        ) {
          const keysDraggedArray = Array.from(
            dndResult.draggedItems.keysDragged
          )

          // 1. sort by sortKey
          const flatSortedTree = treeManager.getFlatSortedTree__EXPENSIVE()
          const sortedKeysDragged = keysDraggedArray.sort((a, b) => {
            const nodeAIndex = flatSortedTree.findIndex(
              (n) =>
                getKey(treeManager.getNode(n.key, { required: true })) === a.id
            )
            const nodeBIndex = flatSortedTree.findIndex(
              (n) =>
                getKey(treeManager.getNode(n.key, { required: true })) === b.id
            )
            return nodeAIndex - nodeBIndex
          })

          // 2. apply sort and filter mutations
          dndResult.draggedItems.keysDragged = new Set(sortedKeysDragged)

          // 3. call onDragEnd
          rest.onDragEnd(dndResult)
        }
      }

      treeEl.addEventListener('dnd', handleDnd)
      return () => {
        treeEl.removeEventListener('dnd', handleDnd)
      }
    }, [rest, treeManager, getKey])

    const lastAutoFocusedKey = React.useRef<string>(undefined)
    // get path of autoFocusKey so we can find it
    React.useEffect(() => {
      if (autoFocusKey) {
        if (lastAutoFocusedKey.current === autoFocusKey) return
        const path = treeManager.getPath(autoFocusKey, {
          format: 'array',
        })
        if (path) {
          setAutoFocusPath(path)
          lastAutoFocusedKey.current = autoFocusKey
        }
      }
    }, [autoFocusKey, treeManager])

    React.useEffect(() => {
      return () => {
        lastAutoFocusedKey.current = undefined
      }
    }, [])

    const setSelectedKeys_COOL = React.useCallback(
      (keys: Set<{ label: string; id: string }>) => {
        const keysArray = Array.from(keys)

        // 1. filter keys where parents are already included
        // basically: if any key is child of someone up on their path
        const filteredKeysDragged = keysArray.filter(({ id }) => {
          const path = treeManager.getPath(id, {
            format: 'array',
            required: true,
          })
          if (!path) throw new Error(`node ${id} not found`)
          const includesPath = path.some((p) => {
            if (p === id) return false
            return keysArray.some(({ id }) => id === p)
          })
          return !includesPath
        })

        // 2. call upstream setSelectedKeys_DONT_USE_DIRECTLY with keys filtered
        // we don't sort here because we want to preserve the order of the keys
        // we sort in the drag and drop handler
        setSelectedKeys_DONT_USE_DIRECTLY(new Set(filteredKeysDragged))
      },
      [setSelectedKeys_DONT_USE_DIRECTLY, treeManager]
    )

    const collapseAllCallbackListRef = React.useRef<Map<string, () => void>>(
      new Map()
    )

    const onAutoFocusKeyFocusRef = React.useRef(onAutoFocusKeyFocus)
    onAutoFocusKeyFocusRef.current = onAutoFocusKeyFocus

    const _onAutoFocusKeyFocus = React.useCallback(() => {
      setAutoFocusPath([])
      onAutoFocusKeyFocusRef.current?.()
    }, [])

    return (
      <treeContext.Provider
        value={{
          // general
          treeId,
          treeKey,
          treeRef,
          treeManager,
          getKey,
          dragAndDrop: rest.dragAndDrop,
          allowDrop:
            rest.dragAndDrop === 'enabled' ? rest.allowDrop : undefined,
          isCollapsed,
          setIsCollapsed,
          autoFocusPath,
          autoFocusKey,
          onAutoFocusKeyFocus: _onAutoFocusKeyFocus,
          // create new child
          isCreatingChild,
          setIsCreatingChild,
          onCreateNewChild,
          // active
          activeKey,
          onActiveKeyChange: (
            { label, id }: { label: string; id: string },
            { doubleClick }
          ) => {
            onActiveKeyChange({ label, id }, { doubleClick })
          },
          // selection
          selectedKeys,
          onSelectKeyToggle: ({ label, id }) => {
            const newSet = Array.from(selectedKeys)
            const index = newSet.findIndex((k) => k.id === id)
            if (index !== -1) newSet.splice(index, 1)
            else newSet.push({ label, id })
            setSelectedKeys_COOL(new Set(newSet))
          },
          onSelectKeys: (keys) => {
            setSelectedKeys_COOL(new Set(keys))
          },
          onSelectKeyRange: (start, end) => {
            if (start === end) {
              // select single key
              setSelectedKeys_COOL(new Set([start]))
            } else {
              const flatTree = treeManager.getFlatSortedTree__EXPENSIVE()

              let startIndex = flatTree.findIndex(
                (n) =>
                  getKey(treeManager.getNode(n.key, { required: true })) ===
                  start.id
              )
              let endIndex = flatTree.findIndex(
                (n) =>
                  getKey(treeManager.getNode(n.key, { required: true })) ===
                  end.id
              )
              if (startIndex === -1) {
                throw new Error(`Start index with key ${start.id} not found`)
              }
              if (endIndex === -1) {
                throw new Error(`End index with key ${end.id} not found`)
              }
              let reversed = false
              if (startIndex > endIndex) {
                const auxStartIndex = startIndex
                startIndex = endIndex
                endIndex = auxStartIndex
                reversed = true
              }
              const keysToAdd: { label: string; id: string }[] = []
              for (let index = startIndex; index < endIndex + 1; index++) {
                const nodeKey = flatTree[index]?.key
                const node = nodeKey ? treeManager.getNode(nodeKey) : null
                if (node) {
                  if (reversed) {
                    keysToAdd.unshift({
                      label: node.value.title,
                      id: getKey(node),
                    })
                  } else {
                    keysToAdd.push({
                      label: node.value.title,
                      id: getKey(node),
                    })
                  }
                }
              }
              setSelectedKeys_COOL(new Set(keysToAdd))
            }
          },
          onUnselectAll: () => {
            setSelectedKeys_COOL(new Set())
          },
          // focus stuff
          onFocusPrevListItem: (item: HTMLButtonElement) => {
            const treeList = item.closest<HTMLDivElement>('[data-tree-list]')
            if (!treeList) throw new Error('tree list not found')
            const allExpandedDocs = Array.from(
              treeList.querySelectorAll<HTMLButtonElement>(
                'button[data-tree-list-item]'
              )
            )
            const focusedDocIndex = allExpandedDocs.findIndex(
              (el) => el === item
            )
            const prevDoc =
              allExpandedDocs[focusedDocIndex - 1] ??
              allExpandedDocs[allExpandedDocs.length - 1]
            if (prevDoc) {
              prevDoc.focus()
            }
          },
          onFocusNextListItem: (item: HTMLButtonElement) => {
            const treeList = item.closest<HTMLDivElement>('[data-tree-list]')
            if (!treeList) throw new Error('tree list not found')
            const allExpandedDocs = Array.from(
              treeList.querySelectorAll<HTMLButtonElement>(
                'button[data-tree-list-item]'
              )
            )
            const focusedDocIndex = allExpandedDocs.findIndex(
              (el) => el === item
            )
            const nextDoc =
              allExpandedDocs[focusedDocIndex + 1] ?? allExpandedDocs[0]
            if (nextDoc) {
              nextDoc.focus()
            }
          },
          onFocusParent: (item) => {
            const itemListChildContainer = item.closest<HTMLDivElement>(
              '[data-tree-list-child]'
            )?.parentElement
            if (!itemListChildContainer) {
              throw new Error('itemListChildContainer not found')
            }
            const parentListChildContainer =
              itemListChildContainer?.closest<HTMLDivElement>(
                '[data-tree-list-child]'
              )
            if (!parentListChildContainer) {
              throw new Error('parentListChildContainer not found')
            }
            const parent =
              parentListChildContainer?.querySelector<HTMLButtonElement>(
                '[data-tree-list-item]'
              )
            if (!parent) throw new Error('parent not found')
            parent.focus()
          },
          onFocusFirstChild: (item) => {
            const itemListChild = item.closest<HTMLDivElement>(
              '[data-tree-list-child]'
            )
            if (!itemListChild) {
              throw new Error('itemListChild not found')
            }
            const allChildren =
              itemListChild?.querySelectorAll<HTMLButtonElement>(
                '[data-tree-list-item]'
              )
            if (!allChildren) throw new Error('allChildren not found')
            if (allChildren.length <= 1) {
              // do nothing
            } else {
              allChildren.item(1).focus()
            }
          },
          // collapse all
          onCollapseAll: () => {
            collapseAllCallbackListRef.current.forEach((cb) => cb())
          },
          addCallbackCollapseAllList: (key: string, cb: () => void) => {
            collapseAllCallbackListRef.current.set(key, cb)
          },
        }}
      >
        <div
          className={clsx('group/tree flex flex-shrink-0 flex-col', className)}
          ref={mergeRefs([treeRef, ref])}
          data-tree
        >
          {children}
        </div>
      </treeContext.Provider>
    )
  }
)

/* -------------------------------------------------------------------------------------------------
 * TreeTitle
 * -----------------------------------------------------------------------------------------------*/

type TreeTitleProps = {
  children?: React.ReactNode
  nestedItemsQuantity: number
  disableCollapseAll?: boolean
  preActions?: React.ReactNode
  postActions?: React.ReactNode
  showActionsOnHover?: boolean
  disableAddBelow?: boolean
}

const TreeTitle = ({
  children,
  nestedItemsQuantity,
  disableCollapseAll,
  preActions,
  postActions,
  showActionsOnHover = true,
  disableAddBelow,
}: TreeTitleProps) => {
  const {
    isCollapsed,
    setIsCollapsed,
    setIsCreatingChild,
    onCreateNewChild,
    onCollapseAll,
  } = useTreeContext()

  return (
    <div className="relative flex items-center justify-between px-3 mb-1">
      <div className="select-none truncate flex items-center">
        <Button
          onClick={() => {
            setIsCollapsed((p) => !p)
          }}
          className="p-1 text-xs font-medium leading-none rounded-md cursor-default text-dark-muted outline-1 outline-dark-control-base hover:bg-dark-shade-hover focus-visible:outline"
          unstyled
        >
          <span>{children}</span>
        </Button>
        {isCollapsed && (
          <span className="ml-1 text-xs leading-4 text-dark-faint">
            ({nestedItemsQuantity})
          </span>
        )}
      </div>
      <div
        className={clsx(
          'flex space-x-1 pr-[9px] items-center',
          showActionsOnHover &&
            'opacity-0 group-hover/tree:opacity-100 transition-opacity duration-100'
        )}
      >
        {preActions}
        {onCreateNewChild && !disableAddBelow && (
          <SimpleTooltip content="Add below">
            <span
              className={$actionButton()}
              role="button"
              onClick={(e) => {
                e.stopPropagation()
                setIsCreatingChild(blockTypesRecord.document)
                setIsCollapsed(false)
              }}
              aria-label="add document inside"
            >
              <PlusIcon />
            </span>
          </SimpleTooltip>
        )}
        {!disableCollapseAll && (
          <SimpleTooltip content="Collapse all">
            <span
              className={$actionButton()}
              role="button"
              onClick={(e) => {
                e.stopPropagation()
                onCollapseAll()
              }}
              aria-label="collapse all"
            >
              <CollapseIcon />
            </span>
          </SimpleTooltip>
        )}
        {postActions}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * TreeList
 * -----------------------------------------------------------------------------------------------*/

type TreeListProps = {
  children?: React.ReactNode
  onFocus?: () => void
  papadaSize?: 'base' | 'large'
  repoUndoStuff?: {
    undoManager: RepoUndoManager | null
    encodedSegment: string | undefined
  }
}

const TreeList = ({
  children,
  onFocus,
  papadaSize = 'base',
  repoUndoStuff,
}: TreeListProps) => {
  const {
    onUnselectAll,
    dragAndDrop,
    isCollapsed,
    onCreateNewChild,
    setIsCreatingChild,
    setIsCollapsed,
    treeManager,
  } = useTreeContext()

  const canChangeSchema = useIsMemberOrHigher()
  const editModeIsEnabled = useUserPreferences((s) => s.editMode)
  const addTab = useTabsStore((s) => s.addTab)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const cutBlocks = useRepoStore((s) => s.cutBlocks)
  const setCutBlocks = useRepoStore((s) => s.setCutBlocks)
  const replicacheData = useRepoReplicache().data
  const replicache = replicacheData?.replicache

  if (isCollapsed) return null
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          className="relative flex flex-grow flex-col border border-[transparent] px-3 focus:outline-none"
          onFocus={(e) => {
            if (document.activeElement === e.currentTarget) {
              onUnselectAll()
            }
            onFocus?.()
          }}
          onKeyDown={(e) => {
            if (document.activeElement === e.currentTarget) {
              if (isHotkey(['ArrowUp', 'ArrowDown', 'ArrowRight'], e)) {
                e.preventDefault()
                e.stopPropagation()
                e.currentTarget
                  .querySelector<HTMLButtonElement>('[data-tree-list-item]')
                  ?.focus()
              }
            }
          }}
          tabIndex={0}
          data-tree-list
        >
          {children}
          {dragAndDrop === 'enabled' || dragAndDrop === 'enabled-drop-after' ? (
            // this gives the tree some paddingBottom
            // and also serves as a min height for the drop after target to have.
            <TreeListAfterDropTarget
              className={clsx(
                papadaSize === 'base' && 'min-h-3',
                papadaSize === 'large' && 'min-h-16'
              )}
            />
          ) : (
            // this gives the tree some paddingBottom.
            <div className="h-4" aria-hidden />
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {onCreateNewChild ? (
          <>
            <ContextMenuGroup>
              <ContextMenuItem
                onSelect={() => {
                  setIsCreatingChild(blockTypesRecord.document)
                  setIsCollapsed(false)
                }}
                className="space-x-2"
              >
                <BlockIcon block={{ type: blockTypesRecord.document }} />
                <span>Add Document</span>
              </ContextMenuItem>
              <ContextMenuItem
                onSelect={() => {
                  setIsCreatingChild(blockTypesRecord.component)
                  setIsCollapsed(false)
                }}
                className="space-x-2"
              >
                <BlockIcon block={{ type: blockTypesRecord.component }} />
                <span>Add Component</span>
              </ContextMenuItem>
              <ContextMenuItem
                onSelect={() => {
                  setIsCreatingChild(blockTypesRecord.list)
                  setIsCollapsed(false)
                }}
                className="space-x-2"
              >
                <BlockIcon block={{ type: blockTypesRecord.list }} />
                <span>Add Collection</span>
              </ContextMenuItem>
            </ContextMenuGroup>
            {canChangeSchema && (
              <>
                <ContextMenuSeparator />
                <ContextMenuGroup>
                  <ContextMenuItem
                    onSelect={async () => {
                      if (!replicache || !treeManager) return
                      const pasteResult = await pasteBlocksFromClipboard({
                        repoId: replicacheData.repoId,
                        addTab,
                        repoStore: { cutBlocks, setCutBlocks },
                        editModeIsEnabled,
                        replicache,
                        selectedBlock: null,
                        destinationPath: treeManager.root.key,
                        blockTreeManager: treeManager as TreeManager<
                          Block,
                          NonNullable<unknown>
                        >,
                        shiftKeyPressed: false,
                      })
                      if (
                        repoUndoStuff?.undoManager &&
                        pasteResult?.newBlock &&
                        pasteResult?.undo
                      ) {
                        const focusOp = {
                          op: 'focus',
                          args: {
                            focus: {
                              blockId: idToSourceBlockId(pasteResult.newBlock),
                              type: 'block:sidebar',
                            },
                            preferredTabKey: activeTabKey,
                          },
                        } as const
                        repoUndoStuff.undoManager.push({
                          redo: [pasteResult.redo, focusOp],
                          undo: [
                            {
                              op: 'blockBatchMutations',
                              args: { mutations: pasteResult.undo },
                            },
                            focusOp,
                          ],
                        })
                      }
                    }}
                    className="space-x-2"
                  >
                    <ClipboardIcon />
                    <span>Paste</span>
                  </ContextMenuItem>
                </ContextMenuGroup>
              </>
            )}
          </>
        ) : (
          <ContextMenuLabel className="!py-1.5 text-dark-muted">
            No actions to perform.
          </ContextMenuLabel>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Item Context
 * -----------------------------------------------------------------------------------------------*/

type IsCreatingChild =
  | DocumentBlock['type']
  | ComponentBlock['type']
  | ListBlock['type']
  | false

const itemContext = React.createContext<
  | {
      isParentSelected: boolean
      level: number
      node: TreeNode<Block | BlockChange>
      index: number
      isActive: boolean
      isRenaming: boolean
      isExpanded: boolean
      isCreatingChild: IsCreatingChild
      expandDisabled: boolean
      setIsRenaming: (isRenaming: boolean) => void
      setIsExpanded: (isExpanded: boolean) => void
      setIsCreatingChild: (isCreatingChild: IsCreatingChild) => void
      setExpandDisabled: (value: boolean) => void
    }
  | undefined
>(undefined)

const useItemContext = () => {
  const ctx = React.useContext(itemContext)
  if (!ctx) throw new Error('itemContext not found')
  return ctx
}

const ItemContextProvider = ({
  children,
  isParentSelected,
  level,
  isActive,
  index,
  node,
}: {
  children: React.ReactNode
  isParentSelected: boolean
  level: number
  index: number
  isActive: boolean
  node: TreeNode<Block | BlockChange>
}) => {
  const [isRenaming, setIsRenaming] = React.useState(false)
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isCreatingChild, setIsCreatingChild] =
    React.useState<IsCreatingChild>(false)
  const [expandDisabled, setExpandDisabled] = React.useState(false)

  const noop = React.useCallback(() => undefined, [])

  return (
    <itemContext.Provider
      value={{
        isParentSelected,
        level,
        node,
        index,
        isRenaming,
        isExpanded: expandDisabled ? false : isExpanded,
        isCreatingChild,
        setIsRenaming,
        setIsExpanded: expandDisabled ? noop : setIsExpanded,
        expandDisabled,
        setExpandDisabled,
        isActive,
        setIsCreatingChild: (isCreatingChild) => {
          if (isCreatingChild) {
            setIsExpanded(true)
          }

          setIsCreatingChild(isCreatingChild)
        },
      }}
    >
      {children}
    </itemContext.Provider>
  )
}

/* -------------------------------------------------------------------------------------------------
 * TreeListChild
 * -----------------------------------------------------------------------------------------------*/

type Group = {
  key: string
  title: React.ReactNode
  icon?: React.ReactNode
  dim?: boolean
  isActive: boolean
  onClick: (group: Group, opts: { doubleClick: boolean }) => void
}

export type ChildProps<NodeType extends Block | BlockChange> = {
  child: TreeNode<NodeType>
  setIsRenaming: (isRenaming: boolean) => void
  selectedKeys: Set<{ label: string; id: string }>
  isSelected: boolean
  childrenToRender: TreeNode<NodeType>[] | null
}

export type TreeListChildProps<NodeType extends Block | BlockChange> = {
  parent: TreeNode<NodeType>
  children: (props: ChildProps<NodeType>) => React.ReactNode
  emptyState: React.ReactNode | ((level: number) => React.ReactNode)
  level: number
  getChildrenToRender?: (
    parent: TreeNode<NodeType>,
    level: number,
    group: Group | null | undefined
  ) => TreeNode<NodeType>[] | null
  getGroups?: (
    parent: TreeNode<NodeType>,
    level: number,
    opts: { isParentActive: boolean }
  ) => Group[] | null
  isFirstChild?: boolean
  id?: string
  defaultSelectFirst?: boolean
  renderControllerRef?: React.RefObject<Record<string, () => void>>
  shouldForceExpanded?: (key: string) => boolean
}

const TreeListChild = <NodeType extends Block | BlockChange>({
  parent,
  children: Children,
  emptyState,
  level,
  getChildrenToRender,
  getGroups,
  isFirstChild,
  defaultSelectFirst,
  id,
  renderControllerRef,
  shouldForceExpanded,
}: TreeListChildProps<NodeType>) => {
  const [_, rerender] = React.useReducer((x) => x + 1, 0)
  const {
    dragAndDrop,
    selectedKeys,
    activeKey,
    getKey,
    onCreateNewChild,
    isCreatingChild: isRootCreatingChild,
    setIsCreatingChild: setIsRootCreatingChild,
    onFocusNextListItem,
    onFocusPrevListItem,
  } = useTreeContext()

  const ctx = React.useContext(itemContext)
  const isRoot = level === 0

  const parentKey = getKey(parent)
  const isSelectedIndividually = React.useMemo(
    () => Array.from(selectedKeys).some(({ id }) => id === parentKey),
    [parentKey, selectedKeys]
  )
  const isSelected = React.useMemo(
    () => isSelectedIndividually || (ctx?.isParentSelected ?? false),
    [ctx?.isParentSelected, isSelectedIndividually]
  )

  const getIsActive = React.useCallback(
    ({
      level,
      isFirstChild,
      key,
    }: {
      level: number
      isFirstChild: boolean
      key: string
    }) => {
      if (!activeKey && defaultSelectFirst && level === 1 && isFirstChild) {
        return true
      }
      return activeKey?.id === key
    },
    [activeKey, defaultSelectFirst]
  )

  const isActive = React.useMemo(() => {
    return getIsActive({ level, isFirstChild: !!isFirstChild, key: parentKey })
  }, [getIsActive, isFirstChild, level, parentKey])

  const groups =
    getGroups?.(parent, level, { isParentActive: isActive }) ?? null
  const childrenToRenderRoot = groups?.length
    ? null
    : (getChildrenToRender?.(parent, level, null) ?? null)

  if (!isRoot && !ctx) {
    throw new Error('itemContext not found, and it should be')
  }
  const isExpanded = React.useMemo(() => {
    // we now know that ctx is defined if we are not in the root.
    if (typeof ctx?.isExpanded !== 'boolean') return true
    if (shouldForceExpanded?.(parentKey)) return true

    return ctx.isExpanded
  }, [ctx?.isExpanded, parentKey, shouldForceExpanded])

  const setIsRenaming = React.useMemo(
    () => ctx?.setIsRenaming ?? (() => undefined),
    [ctx?.setIsRenaming]
  )
  const isCreatingChild = React.useMemo(
    () => ctx?.isCreatingChild ?? isRootCreatingChild,
    [ctx?.isCreatingChild, isRootCreatingChild]
  )

  React.useImperativeHandle(renderControllerRef, () => {
    return {
      ...renderControllerRef?.current,
      [`r_${parentKey}`]: () => {
        rerender()
      },
    }
  })

  return (
    <div
      data-tree-list-child={parent.key}
      data-tree-list-child-level={level}
      data-tree-list-child-active={isActive}
      id={id}
      className={clsx(
        'relative',
        !isRoot && 'mt-0.5',
        // small css hack: i don't want the first item of the first level to have margin top (because of a ui nitpick :))
        level === 1 && 'first-of-type:mt-0'
      )}
    >
      {!isRoot ? (
        <Children
          child={parent}
          setIsRenaming={setIsRenaming}
          selectedKeys={selectedKeys}
          isSelected={isSelected}
          childrenToRender={childrenToRenderRoot}
        />
      ) : null}
      {(isExpanded || isRoot) && (
        <div
          className="relative"
          style={{
            ['--pl' as string]: level * 16 + 'px',
          }}
        >
          <span
            className="absolute top-1.5 bottom-1.5 z-10 border-l border-dark-border-solid"
            style={{
              left: `calc(var(--pl) - 7px)`,
              display: level === 0 ? 'none' : 'block',
            }}
          />
          {isCreatingChild && (
            <TreeListItemCreateChild
              onCreate={async (title) => {
                await onCreateNewChild?.(parent, {
                  title,
                  type: isCreatingChild,
                })
                if (ctx) {
                  ctx?.setIsCreatingChild(false)
                } else {
                  setIsRootCreatingChild(false)
                }
              }}
              onCancel={() => {
                if (ctx) {
                  ctx?.setIsCreatingChild(false)
                } else {
                  setIsRootCreatingChild(false)
                }
              }}
            />
          )}
          {groups?.length ? (
            groups.map((group) => {
              let childrenToRender
              try {
                childrenToRender =
                  getChildrenToRender?.(parent, level, group) ?? []
              } catch (e) {
                console.warn(
                  `Error while getting children to render for group ${group.key}:`,
                  e
                )
                return null
              }

              return (
                <div key={group.key}>
                  <div style={{ paddingLeft: 'var(--pl)' }} className="mt-0.5">
                    <Button
                      unstyled
                      className={$treeListItem({ isActive: group.isActive })}
                      onClick={(event) =>
                        group.onClick(group, {
                          doubleClick: event.detail === 2,
                        })
                      }
                      onKeyDown={(e) => {
                        if (isHotkey('ArrowUp', e)) {
                          e.preventDefault()
                          e.stopPropagation()
                          onFocusPrevListItem(e.currentTarget)
                        } else if (isHotkey('ArrowDown', e)) {
                          e.preventDefault()
                          e.stopPropagation()
                          onFocusNextListItem(e.currentTarget)
                        } else if (isHotkey('ArrowRight', e)) {
                          e.preventDefault()
                          e.stopPropagation()
                          onFocusNextListItem(e.currentTarget)
                        } else if (isHotkey('ArrowLeft', e)) {
                          e.preventDefault()
                          e.stopPropagation()
                          onFocusPrevListItem(e.currentTarget)
                        }
                      }}
                      data-tree-list-item
                    >
                      <TreeListItemIcon
                        //  className="ml-[13px] text-dark-faint"
                        className="ml-[7px] text-dark-faint"
                      >
                        {group.icon || (
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 15 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.25 7.5C9.25 8.4665 8.4665 9.25 7.5 9.25C6.5335 9.25 5.75 8.4665 5.75 7.5C5.75 6.5335 6.5335 5.75 7.5 5.75C8.4665 5.75 9.25 6.5335 9.25 7.5Z"
                              fill="currentColor"
                            />
                          </svg>
                        )}
                      </TreeListItemIcon>
                      <span
                        className={clsx(
                          group.dim && 'opacity-50',
                          'overflow-hidden text-ellipsis whitespace-nowrap'
                        )}
                      >
                        {group.title}
                      </span>
                    </Button>
                  </div>
                  <div className="pl-[0px]">
                    {childrenToRender.map((child, i) => {
                      const childLevel = level + 1
                      const isFirstChild = i === 0
                      return (
                        <ItemContextProvider
                          key={getKey(child)}
                          isParentSelected={isSelected}
                          isActive={getIsActive({
                            key: getKey(child),
                            isFirstChild,
                            level: childLevel,
                          })}
                          level={childLevel}
                          node={child}
                          index={i}
                        >
                          <TreeListChild
                            key={getKey(child)}
                            parent={child}
                            emptyState={emptyState}
                            level={childLevel}
                            getChildrenToRender={getChildrenToRender}
                            getGroups={getGroups}
                            shouldForceExpanded={shouldForceExpanded}
                            isFirstChild={isFirstChild}
                            defaultSelectFirst={defaultSelectFirst}
                            renderControllerRef={renderControllerRef}
                          >
                            {Children}
                          </TreeListChild>
                        </ItemContextProvider>
                      )
                    })}
                  </div>
                </div>
              )
            })
          ) : childrenToRenderRoot?.length ? (
            childrenToRenderRoot.map((child, i) => {
              const childLevel = level + 1
              const isFirstChild = i === 0

              return (
                <ItemContextProvider
                  key={getKey(child)}
                  isParentSelected={isSelected}
                  level={childLevel}
                  isActive={getIsActive({
                    key: getKey(child),
                    isFirstChild,
                    level: childLevel,
                  })}
                  node={child}
                  index={i}
                >
                  <TreeListChild
                    key={getKey(child)}
                    parent={child}
                    emptyState={emptyState}
                    level={childLevel}
                    getChildrenToRender={getChildrenToRender}
                    getGroups={getGroups}
                    shouldForceExpanded={shouldForceExpanded}
                    isFirstChild={isFirstChild}
                    defaultSelectFirst={defaultSelectFirst}
                    renderControllerRef={renderControllerRef}
                  >
                    {Children}
                  </TreeListChild>
                </ItemContextProvider>
              )
            })
          ) : isCreatingChild ? null : (
            <div
              className="font-medium flex min-h-7 select-none items-center px-4 text-xs text-dark-muted text-pretty"
              style={{
                paddingLeft: 'calc(var(--pl) + 6px)',
              }}
            >
              {typeof emptyState === 'function'
                ? emptyState(level)
                : emptyState}
            </div>
          )}
        </div>
      )}

      {dragAndDrop === 'enabled' && (
        <TreeListChildDropTarget node={parent} level={level} />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * TreeListItem
 * -----------------------------------------------------------------------------------------------*/

// opacity-100 because dnd-kit puts opacity: 0 onDragEnd, odly.
export const $treeListItem = cva(
  '!opacity-100 group relative text-dark-dim flex h-7 w-full cursor-default select-none items-center space-x-1.5 rounded pr-4 text-xs focus:outline-none focus-visible:shadow-focus',
  {
    variants: {
      isActive: {
        false: 'text-dark-dim hover:bg-dark-shade/60',
        true: 'bg-dark-shade-hover text-dark-bright',
      },
    },
    defaultVariants: {
      isActive: false,
    },
  }
)

type TreeListItemProps = {
  children?: React.ReactNode
  rename?: 'disabled' | 'enabled'
  contextMenuActions?: React.ReactNode
  draggable?: boolean
}

const TreeListItem = React.memo(
  ({
    children,
    rename = 'enabled',
    contextMenuActions,
    draggable: isDraggable = true,
  }: TreeListItemProps) => {
    const {
      treeId,
      activeKey,
      onActiveKeyChange,
      selectedKeys,
      onSelectKeyToggle,
      onSelectKeyRange,
      onSelectKeys,
      onUnselectAll,
      onFocusPrevListItem,
      onFocusNextListItem,
      onFocusParent,
      onFocusFirstChild,
      dragAndDrop,
      autoFocusPath,
      onAutoFocusKeyFocus,
      treeRef,
      getKey,
    } = useTreeContext()

    const {
      isParentSelected,
      node,
      isExpanded,
      setIsExpanded,
      setIsRenaming,
      isRenaming,
      isActive,
    } = useItemContext()

    const nodeKey = getKey(node)

    // const isActive = React.useMemo(
    //   () => activeKey?.id === nodeKey,
    //   [activeKey?.id, nodeKey]
    // )
    const selectedKeysAsArray = React.useMemo(
      () => Array.from(selectedKeys),
      [selectedKeys]
    )
    const isSelected = React.useMemo(
      () =>
        selectedKeysAsArray.some(({ id }) => nodeKey === id) ||
        isParentSelected,
      [isParentSelected, nodeKey, selectedKeysAsArray]
    )
    const isDraggableDisabled = React.useDeferredValue(!isDraggable)
    const draggable = useDraggable({
      id: `tree-${treeId}-list-item/${nodeKey}`,
      data: dndControllers.draggableDataSchema.parse({
        // if the key is selected, pass on the whole selection
        // if the key is not selected, pass only the single key
        // vscode works this way.
        keysDragged: isSelected
          ? selectedKeys
          : new Set([{ label: node.value.title, id: nodeKey }]),
        type: dndType,
      }),
      disabled: isDraggableDisabled,
    })

    const [shouldScrollIntoViewIfActive, setShouldScrollIntoViewIfActive] =
      React.useState(true)

    // auto expand based on props
    React.useEffect(() => {
      if (autoFocusPath.length) {
        const indexOfNode = autoFocusPath.indexOf(node.key)
        if (indexOfNode !== -1) {
          const isTarget = indexOfNode === autoFocusPath.length - 1
          if (isTarget) {
            // and then clear the autoExpand state
            onAutoFocusKeyFocus()
            setShouldScrollIntoViewIfActive(true)
          } else {
            // expand so we keep going down
            setIsExpanded(true)
          }
        }
      }
    }, [autoFocusPath, node.key, onAutoFocusKeyFocus, setIsExpanded])

    React.useEffect(() => {
      if (isActive && shouldScrollIntoViewIfActive) {
        setShouldScrollIntoViewIfActive(false)
        const button = treeRef.current?.querySelector<HTMLButtonElement>(
          `[data-tree-list-item="${node.key}"]`
        )
        if (button) {
          setTimeout(() => {
            button.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }, 250)
        }
      }
    }, [isActive, node.key, shouldScrollIntoViewIfActive, treeRef])

    const setFocus = useRepoStore((s) => s.setFocus)

    return (
      <ContextMenuWrapper
        enabled={!!contextMenuActions}
        actions={contextMenuActions}
        onSelect={() => {
          setFocus({
            blockId: nodeKey,
            type: 'block:sidebar',
          })
        }}
      >
        <div style={{ paddingLeft: 'var(--pl)' }}>
          <Button
            unstyled
            className={$treeListItem({ isActive: isActive || isSelected })}
            onClick={(e) => {
              if (isRenaming) {
                // weirdly, if i press "space" when renaming an input as a child of this button, onClick will be fired.
                e.preventDefault()
                return
              }

              if (isActive) {
                // for now, we won't let activeKeys from being toggled, as it might be confusing.
                return
              }

              if (e.shiftKey) {
                const [first] = selectedKeys
                const start = first ?? activeKey
                if (start) {
                  onSelectKeyRange(start, {
                    label: node.value.title,
                    id: nodeKey,
                  })
                } else {
                  onSelectKeys([{ label: node.value.title, id: nodeKey }])
                }
              } else if (commandKeyPressed(e)) {
                if (
                  activeKey &&
                  !selectedKeysAsArray.some(
                    ({ id }) =>
                      id === activeKey.id || (id === nodeKey && isActive)
                  )
                ) {
                  // also select active key if it's not selected
                  onSelectKeys([
                    { label: node.value.title, id: nodeKey },
                    activeKey,
                  ])
                } else {
                  onSelectKeyToggle({ label: node.value.title, id: nodeKey })
                }
              } else {
                onUnselectAll()
                onActiveKeyChange(
                  { label: node.value.title, id: nodeKey },
                  { doubleClick: false }
                )
              }
            }}
            onDoubleClick={() => {
              onUnselectAll()
              onActiveKeyChange(
                { label: node.value.title, id: nodeKey },
                { doubleClick: true }
              )
            }}
            onKeyDown={(e) => {
              if (isHotkey('ArrowUp', e)) {
                e.preventDefault()
                e.stopPropagation()
                onFocusPrevListItem(e.currentTarget)
              } else if (isHotkey('ArrowDown', e)) {
                e.preventDefault()
                e.stopPropagation()
                onFocusNextListItem(e.currentTarget)
              } else if (isHotkey('ArrowRight', e)) {
                e.preventDefault()
                e.stopPropagation()
                if (isExpanded) {
                  onFocusFirstChild(e.currentTarget)
                } else {
                  setIsExpanded(true)
                }
              } else if (isHotkey('ArrowLeft', e)) {
                e.preventDefault()
                e.stopPropagation()
                if (isExpanded) {
                  setIsExpanded(false)
                } else {
                  onFocusParent(e.currentTarget)
                }
              } else if (isHotkey('Enter', e)) {
                if (rename === 'disabled') return
                e.preventDefault()
                e.stopPropagation()
                setIsRenaming(true)
              } else if (isHotkey('Escape', e)) {
                e.preventDefault()
                e.stopPropagation()
                if (draggable.active) {
                  return
                }
                if (selectedKeys.size > 0) {
                  onUnselectAll()
                } else {
                  e.currentTarget
                    .closest<HTMLDivElement>('[data-tree-list]')
                    ?.focus()
                }
              }
            }}
            ref={dragAndDrop === 'enabled' ? draggable.setNodeRef : undefined}
            {...(dragAndDrop === 'enabled' ? draggable.attributes : undefined)}
            {...(dragAndDrop === 'enabled' ? draggable.listeners : undefined)}
            data-tree-list-item={node.key}
          >
            {children}

            {dragAndDrop === 'enabled' && <TreeListItemDropTopTarget />}
            {dragAndDrop === 'enabled' && !isExpanded && (
              <TreeListItemDropBottomTarget />
            )}
          </Button>
        </div>
      </ContextMenuWrapper>
    )
  }
)

/* -------------------------------------------------------------------------------------------------
 * ContextMenuWrapper
 * -----------------------------------------------------------------------------------------------*/

export type ContextMenuAction =
  | {
      type: 'action'
      icon: React.ReactNode
      label: string
      disabled?: boolean
      onSelect: (e: Event) => void
    }
  | { type: 'separator' }

type ContextMenuWrapperProps = {
  children: React.ReactNode
  actions: React.ReactNode
  enabled: boolean
  onSelect?: (e: React.MouseEvent) => void
}

const ContextMenuWrapper = React.memo(
  ({ children, actions, enabled, onSelect }: ContextMenuWrapperProps) => {
    if (!enabled) {
      return <>{children}</>
    }

    return (
      <ContextMenu>
        <ContextMenuTrigger asChild onContextMenu={onSelect} onClick={onSelect}>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent>{actions}</ContextMenuContent>
      </ContextMenu>
    )
  }
)

/* -------------------------------------------------------------------------------------------------
 * TreeListItemChevron
 * -----------------------------------------------------------------------------------------------*/

const $actionButton = cva(
  'cursor-default rounded-sm p-0.5 hover:bg-dark-elevated text-dark-muted hover:text-dark-dim data-[disabled="true"]:pointer-events-none data-[disabled="true"]:opacity-60'
)

type TreeListItemChevronProps = {
  saveExpandedStateInStorage?: {
    type: 'sessionStorage' | 'localStorage'
    key: string
  }
  disabled?: boolean
  className?: string
  icon?: (props: { isExpanded: boolean }) => React.ReactNode
  defaultExpanded?: boolean
}

const TreeListItemChevron = ({
  saveExpandedStateInStorage,
  disabled,
  className,
  icon,
  defaultExpanded,
}: TreeListItemChevronProps) => {
  const { addCallbackCollapseAllList, treeKey, getKey } = useTreeContext()
  const { isExpanded, setIsExpanded, node, setExpandDisabled } =
    useItemContext()
  const [hasInitializedFromStorage, setHasInitializedFromStorage] =
    React.useState(false)
  const [
    hasInitializedFromDefaultExpanded,
    setHasInitializedFromDefaultExpanded,
  ] = React.useState(false)

  // initialize value from storage
  React.useEffect(() => {
    if (!saveExpandedStateInStorage || hasInitializedFromStorage) return

    const { type, key } = saveExpandedStateInStorage
    const storage = window[type]

    const expandedState = storage.getItem(
      `tree-${treeKey}-expanded-state-${key}`
    )
    if (expandedState) {
      setIsExpanded(Boolean(JSON.parse(expandedState)))
    }

    setHasInitializedFromStorage(true)
  }, [
    saveExpandedStateInStorage,
    setIsExpanded,
    hasInitializedFromStorage,
    treeKey,
  ])

  // sync value into storage
  React.useEffect(() => {
    if (!saveExpandedStateInStorage || !hasInitializedFromStorage) return

    const { type, key } = saveExpandedStateInStorage
    const storage = window[type]

    if (isExpanded) {
      storage.setItem(
        `tree-${treeKey}-expanded-state-${key}`,
        JSON.stringify(true)
      )
    } else {
      storage.removeItem(`tree-${treeKey}-expanded-state-${key}`)
    }
  }, [
    saveExpandedStateInStorage,
    isExpanded,
    hasInitializedFromStorage,
    treeKey,
  ])

  // addCallbackCollapseAllList — in order to collapse all with a single button
  React.useEffect(() => {
    addCallbackCollapseAllList(getKey(node), () => {
      setIsExpanded(false)

      if (saveExpandedStateInStorage) {
        const { type, key } = saveExpandedStateInStorage
        const storage = window[type]
        storage.removeItem(`tree-${treeKey}-expanded-state-${key}`)
      }
    })
  }, [
    getKey,
    addCallbackCollapseAllList,
    node,
    saveExpandedStateInStorage,
    setIsExpanded,
    treeKey,
  ])

  // set default expanded state
  React.useEffect(() => {
    if (!defaultExpanded || hasInitializedFromDefaultExpanded) return
    setHasInitializedFromDefaultExpanded(true)
    let newExpandedState: boolean | undefined = true
    if (saveExpandedStateInStorage) {
      const { type, key } = saveExpandedStateInStorage
      const storage = window[type]
      const expandedStateFromStorage = storage.getItem(
        `tree-${treeKey}-expanded-state-${key}`
      )
      if (
        expandedStateFromStorage &&
        typeof JSON.parse(expandedStateFromStorage) === 'boolean'
      ) {
        newExpandedState = undefined
      }
    }
    if (typeof newExpandedState === 'boolean') {
      setIsExpanded(newExpandedState)
    }
  }, [
    defaultExpanded,
    saveExpandedStateInStorage,
    setIsExpanded,
    treeKey,
    hasInitializedFromDefaultExpanded,
  ])

  React.useEffect(() => {
    if (disabled === undefined) return
    setExpandDisabled(disabled)
  }, [disabled, setExpandDisabled])

  return (
    <span
      role="button"
      onClick={(e) => {
        setIsExpanded(!isExpanded)
        e.stopPropagation()
      }}
      onDoubleClick={(e) => {
        e.stopPropagation()
      }}
      className={$actionButton({
        class: clsx(
          'ml-1 flex items-center justify-center w-[19px] h-[19px]',
          disabled && 'pointer-events-none opacity-50',
          className
        ),
      })}
      aria-label={disabled ? undefined : isExpanded ? 'collapse' : 'expand'}
    >
      {icon ? (
        icon({ isExpanded })
      ) : (
        <ChevronRightIcon className={clsx(isExpanded && 'rotate-90')} />
      )}
    </span>
  )
}

/* -------------------------------------------------------------------------------------------------
 * TreeListItemIcon
 * -----------------------------------------------------------------------------------------------*/

type TreeListItemIconProps = {
  children?: React.ReactNode
  className?: string
}

const TreeListItemIcon = ({ children, className }: TreeListItemIconProps) => {
  return <span className={className}>{children}</span>
}

/* -------------------------------------------------------------------------------------------------
 * TreeListItemTitle
 * -----------------------------------------------------------------------------------------------*/

type TreeListItemTitleProps = {
  children?: React.ReactNode
  rename?: TreeListItemProps['rename']
  className?: string
}

const TreeListItemTitle = ({
  children,
  rename,
  className,
}: TreeListItemTitleProps) => {
  const { isRenaming, setIsRenaming } = useItemContext()

  if (isRenaming) return null // Renders <TreeListItemRenameInput />
  return (
    <p
      className={clsx(
        className,
        "flex-grow overflow-hidden text-ellipsis whitespace-nowrap text-left before:hidden before:content-['Untitled'] empty:before:block"
      )}
      onClick={(e) => {
        if (rename === 'disabled') return
        if (e.detail === 2) setIsRenaming(true)
      }}
    >
      {children}
    </p>
  )
}

/* -------------------------------------------------------------------------------------------------
 * TreeListItemRenameInput
 * -----------------------------------------------------------------------------------------------*/

type TreeListItemRenameInputProps = {
  children?: React.ReactNode
  defaultValue: string
  onRename: (value: string) => Promise<void> | void
}

const TreeListItemRenameInput = ({
  defaultValue,
  onRename,
}: TreeListItemRenameInputProps) => {
  const { isRenaming, setIsRenaming } = useItemContext()
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (isRenaming) {
      // small hack to make sure the input always gets focused
      // e.g, when starting rename from the radix context menu, without this, nothing happens
      setTimeout(() => {
        inputRef.current?.focus()
      }, 16)
    }
  }, [inputRef, isRenaming])

  if (!isRenaming) return null
  return (
    <Input
      ref={inputRef}
      autoFocus
      autoCorrect="off"
      autoComplete="off"
      name="title"
      size={'xs'}
      className="!ml-[4px] !h-full w-[64%] !px-1"
      defaultValue={defaultValue}
      onBlur={async ({ currentTarget }) => {
        // submit
        await onRename(currentTarget.value)
        setIsRenaming(false)
      }}
      onFocus={(e) => {
        e.target.select()
      }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={async (e) => {
        e.stopPropagation()
        const currentTarget = e.currentTarget
        switch (e.key) {
          case 'Enter':
            // submit
            await onRename(currentTarget.value)
            setIsRenaming(false)
            currentTarget.closest('button')?.focus()
            break
          case 'Escape': {
            // exit
            setIsRenaming(false)
            const button = e.currentTarget.closest<HTMLButtonElement>(
              '[data-tree-list-item]'
            )
            if (!button) return
            button.focus()

            break
          }

          default:
            break
        }
      }}
    />
  )
}

/* -------------------------------------------------------------------------------------------------
 * TreeListItemActions
 * -----------------------------------------------------------------------------------------------*/

const TreeListItemActions = ({
  children,
  alwaysVisible,
  plusButtonOptions,
}: {
  children?: React.ReactNode
  alwaysVisible?: boolean
  plusButtonOptions?: {
    variant: 'disabled' | 'enabled-pre' | 'enabled-post'
    dimmed: boolean
  }
}) => {
  const { onCreateNewChild } = useTreeContext()
  const { isRenaming, setIsCreatingChild } = useItemContext()

  const canAdd = Boolean(onCreateNewChild)

  const addActionNode = React.useMemo(() => {
    if (!canAdd) return null
    return (
      <>
        {
          <TreeListItemAction
            ariaLabel="Add block inside"
            onClick={() => {
              setIsCreatingChild(blockTypesRecord.document)
            }}
            icon={
              <PlusIcon
                className={plusButtonOptions?.dimmed ? 'opacity-50' : undefined}
              />
            }
            alwaysVisible={alwaysVisible}
          />
        }
      </>
    )
  }, [alwaysVisible, canAdd, plusButtonOptions?.dimmed, setIsCreatingChild])

  if (isRenaming) return null
  return (
    <div className={clsx('!-mr-2 !ml-auto gap-1 justify-self-end flex')}>
      {plusButtonOptions?.variant === 'enabled-pre' && addActionNode}
      {children}
      {plusButtonOptions?.variant === 'enabled-post' && addActionNode}
    </div>
  )
}

const TreeListItemAction = ({
  icon,
  ariaLabel,
  onClick,
  className,
  alwaysVisible,
}: {
  icon: React.ReactNode
  ariaLabel: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
  className?: string
  alwaysVisible?: boolean
}) => {
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <div
          className={$actionButton({
            class: clsx(
              className,
              'hidden group-hover:flex',
              alwaysVisible && '!flex'
            ),
          })}
          role="button"
          onClick={(e) => {
            e.stopPropagation()
            if (onClick) {
              onClick(e)
            }
          }}
          aria-label={ariaLabel}
        >
          {icon}
        </div>
      </TooltipTrigger>
      <TooltipContent className="py-1 !px-2">{ariaLabel}</TooltipContent>
    </Tooltip>
  )
}

/* -------------------------------------------------------------------------------------------------
 * TreeListItemLabel
 * -----------------------------------------------------------------------------------------------*/

const TreeListItemLabel = ({ children }: { children?: React.ReactNode }) => {
  return (
    <div className={clsx('!-mr-2 !ml-4 flex space-x-1 justify-self-end')}>
      {children}
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * TreeListItemCreateChild
 * -----------------------------------------------------------------------------------------------*/

const TreeListItemCreateChild = ({
  onCreate,
  onCancel,
}: {
  onCreate: (title: string) => void
  onCancel: () => void
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    // small hack to make sure the input always gets focused
    // e.g, when starting rename from the radix context menu, without this, nothing happens
    setTimeout(() => {
      inputRef.current?.focus()
    }, 16)
  }, [inputRef])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        const title = formData.get('title')
        if (typeof title !== 'string') throw new Error('title is required')
        onCreate(title)
      }}
      className="relative z-20 flex items-center h-7 bg-dark-bg"
      style={{
        paddingLeft: 'var(--pl)',
      }}
    >
      <span className={$actionButton()}>
        <PlusIcon />
      </span>
      <Input
        ref={inputRef}
        autoFocus
        autoCorrect="off"
        autoComplete="off"
        name="title"
        size={'xs'}
        className="!ml-[4px] !h-full !px-1"
        placeholder="Enter title"
        onBlur={() => {
          onCancel()
        }}
        onFocus={(e) => {
          e.target.select()
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={async (e) => {
          e.stopPropagation()
          switch (e.key) {
            case 'Escape':
              // exit
              onCancel()
              break

            default:
              break
          }
        }}
        required
      />
    </form>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Drag and drop
 * -----------------------------------------------------------------------------------------------*/

const dndType = 'tree-dnd'

const dndControllers = {
  dndType,
  onDragEnd: (event) => {
    if (!event.over) return
    const parsedDraggable = dndControllers.draggableDataSchema.safeParse(
      event.active.data.current
    )
    const parsedDroppable = dndControllers.droppableDataSchema.safeParse(
      event.over?.data.current
    )

    if (!parsedDraggable.success || !parsedDroppable.success) return

    const droppable = parsedDroppable.data
    const draggable = parsedDraggable.data

    if (droppable.treeNode) {
      const customEvent = new CustomEvent('dnd', {
        detail: {
          draggedItems: draggable,
          dropTarget: droppable,
        },
      })
      droppable.treeNode.dispatchEvent(customEvent)
    }
  },
  droppableDataSchema: z.object({
    newParentKey: z.string().nullable(),
    intendedIndex: z.number(),
    type: z.union([
      z.literal(dndType),
      z.literal(editorDndControllers.dndType),
    ]),
    treeNode: z.any(),
  }),
  draggableDataSchema: z.object({
    keysDragged: z.set(z.object({ label: z.string(), id: z.string() })),
    type: z.union([
      z.literal(dndType),
      z.literal(editorDndControllers.dndType),
    ]),
  }),
} satisfies DndController

/* isValidDnd
 * check stuff like parent - child relationship, etc.
 * -----------------------------------------------------------------------------------------------*/

function isValidDnd(
  treeManager: TreeManagerMethods<Block | BlockChange>,
  keysDragged: z.infer<
    typeof dndControllers.draggableDataSchema
  >['keysDragged'],
  newParentKey: z.infer<
    typeof dndControllers.droppableDataSchema
  >['newParentKey'],
  intendedIndex: number,
  getKey: (node: TreeNode<Block | BlockChange>) => string,
  externalValidation?: (_params: {
    keyDragged: string
    newParentKey: string
  }) => boolean
) {
  if (!newParentKey) return false
  const parentNode = treeManager.getNode(newParentKey)
  if (!parentNode) throw new Error(`parent node ${newParentKey} not found`)
  const children = treeManager.getChildrenKeys(parentNode.key)
  const childAtIntendedIndex = children?.[intendedIndex]
  const nodeAtIntendedIndexKey = childAtIntendedIndex
    ? getKey(treeManager.getNode(childAtIntendedIndex, { required: true }))
    : null

  const isValid = Array.from(keysDragged).every((keyDragged) => {
    if (externalValidation) {
      const passes = externalValidation({
        keyDragged: keyDragged.id,
        newParentKey,
      })
      if (!passes) return false
    }

    if (keyDragged.id === newParentKey) {
      // same item
      return false
    }

    if (nodeAtIntendedIndexKey === keyDragged.id) {
      // same range
      return false
    }

    const keyDraggedNode = treeManager.getNode(keyDragged.id)
    if (!keyDraggedNode) return false

    const path = treeManager.getPath(newParentKey, {
      format: 'array',
      required: true,
    })
    if (path.includes(keyDraggedNode.value.id)) {
      // is parent
      return false
    }

    switch (parentNode?.value.type) {
      case 'instance':
      case 'rich-text':
        return false
      case 'list': {
        if (
          !keyDraggedNode ||
          isChangeBlock(keyDraggedNode.value) ||
          isChangeBlock(parentNode.value) ||
          (keyDraggedNode?.value.type !== 'instance' &&
            keyDraggedNode?.value.type !== 'component')
        ) {
          return false
        }
        if (
          keyDraggedNode?.value.type === 'component' &&
          (parentNode.value.typeOptions as ListBlock['typeOptions'])
            .targetBlockId !== idToSourceBlockId(keyDraggedNode.value)
        ) {
          return false
        }
        if (
          keyDraggedNode?.value.type === 'instance' &&
          keyDraggedNode?.value.typeOptions.targetBlockId !==
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (parentNode.value.typeOptions as any).targetBlockId
        ) {
          return false
        }
      }
    }

    return true
  })
  return isValid
}

/* TreeListChildDropTarget
 * Dropping here should nest the dragged block(s) inside here, at the last index available
 * -----------------------------------------------------------------------------------------------*/

type TreeListChildDropTargetProps = {
  node: TreeNode<Block | BlockChange>
  level: number
}

const TreeListChildDropTarget = ({
  node,
  level,
}: TreeListChildDropTargetProps) => {
  const { treeRef, treeManager, treeId, getKey, allowDrop } = useTreeContext()

  const intendedIndex = treeManager.getChildrenKeys(node.key)?.length ?? 0

  const droppable = useDroppable({
    id: `tree-${treeId}-list-child-child/` + getKey(node),
    data: dndControllers.droppableDataSchema.parse({
      newParentKey: getKey(node),
      intendedIndex,
      type: dndType,
      treeNode: treeRef.current,
    }),
  })

  const isOverAndValid = React.useMemo(() => {
    if (!droppable.active || !droppable.isOver || !droppable.over) return false
    const parsed = dndControllers.draggableDataSchema.safeParse(
      droppable.active.data.current
    )

    if (!parsed.success) return

    const activeItemData = parsed.data

    return isValidDnd(
      treeManager,
      activeItemData.keysDragged,
      node.key,
      intendedIndex,
      getKey,
      allowDrop
    )
  }, [droppable, node, treeManager, intendedIndex, allowDrop, getKey])

  return (
    <div
      className={clsx(
        'pointer-events-none absolute top-0 flex h-full flex-col overflow-hidden rounded-md',
        !isOverAndValid && 'invisible'
      )}
      style={{
        left: `calc(var(--pl) - ${level === 1 ? 8 : 0}px)`,
        right: 0,
      }}
      ref={droppable.setNodeRef}
    >
      <div className="flex-grow w-full bg-dark-control-base/20" />
    </div>
  )
}

/* TreeListItemDropTarget
 * Dropping here should append the dragged block(s) on one index less than the droppable target.
 * -----------------------------------------------------------------------------------------------*/

const TreeListItemDropTopTarget = () => {
  const { treeRef, treeManager, treeId, allowDrop, getKey } = useTreeContext()
  const { node, index } = useItemContext()
  const parent = node.parentKey ? treeManager.getNode(node.parentKey) : null

  const intendedIndex = index

  const droppable = useDroppable({
    id: `tree-top-${treeId}/` + getKey(node),
    data: dndControllers.droppableDataSchema.parse({
      newParentKey: parent ? getKey(parent) : null,
      intendedIndex,
      type: dndType,
      treeNode: treeRef.current,
    }),
    disabled: !parent,
  })

  const isOverAndValid = React.useMemo(() => {
    if (
      !parent?.key ||
      !droppable.active ||
      !droppable.isOver ||
      !droppable.over
    ) {
      return false
    }
    const parsed = dndControllers.draggableDataSchema.safeParse(
      droppable.active.data.current
    )

    if (!parsed.success) return

    const activeItemData = parsed.data

    return isValidDnd(
      treeManager,
      activeItemData.keysDragged,
      parent.key,
      intendedIndex,
      getKey,
      allowDrop
    )
  }, [
    droppable.active,
    droppable.isOver,
    droppable.over,
    treeManager,
    parent?.key,
    intendedIndex,
    getKey,
    allowDrop,
  ])

  return (
    <div
      className={clsx(
        'pointer-events-none absolute -top-px z-10 flex h-[calc(15%+1px)] items-center inset-x-0',
        !isOverAndValid && 'invisible'
      )}
      ref={droppable.setNodeRef}
    >
      <div className="h-0.5 w-full rounded-md absolute -top-px bg-dark-control-base" />
    </div>
  )
}

const TreeListItemDropBottomTarget = React.memo(() => {
  const { treeRef, treeManager, treeId, allowDrop, getKey } = useTreeContext()
  const { node, index } = useItemContext()
  const parent = node.parentKey ? treeManager.getNode(node.parentKey) : null

  const intendedIndex = index + 1

  const droppable = useDroppable({
    id: `tree-bottom-${treeId}/` + getKey(node),
    data: dndControllers.droppableDataSchema.parse({
      newParentKey: parent ? getKey(parent) : null,
      intendedIndex,
      type: dndType,
      treeNode: treeRef.current,
    }),
    disabled: !parent,
  })

  const isOverAndValid = React.useMemo(() => {
    if (
      !parent?.key ||
      !droppable.active ||
      !droppable.isOver ||
      !droppable.over
    ) {
      return false
    }
    const parsed = dndControllers.draggableDataSchema.safeParse(
      droppable.active.data.current
    )

    if (!parsed.success) return

    const activeItemData = parsed.data

    return isValidDnd(
      treeManager,
      activeItemData.keysDragged,
      parent.key,
      intendedIndex,
      getKey,
      allowDrop
    )
  }, [
    droppable.active,
    droppable.isOver,
    droppable.over,
    treeManager,
    parent?.key,
    intendedIndex,
    getKey,
    allowDrop,
  ])

  return (
    <div
      className={clsx(
        'pointer-events-none absolute -bottom-px z-10 flex h-[calc(15%+1px)] items-center inset-x-0',
        !isOverAndValid && 'invisible'
      )}
      ref={droppable.setNodeRef}
    >
      <div className="h-0.5 w-full rounded-md absolute -bottom-px bg-dark-control-base" />
    </div>
  )
})

/* TreeListAfterDropTarget
 * After will always drop to the rootNode, at the last index available
 * -----------------------------------------------------------------------------------------------*/

const TreeListAfterDropTarget = ({ className }: { className?: string }) => {
  const { treeRef, treeManager, treeId, allowDrop, getKey } = useTreeContext()

  const intendedIndex =
    treeManager.getChildrenKeys(treeManager.root.key)?.length ?? 0

  const droppable = useDroppable({
    id: `tree-${treeId}-after/` + treeManager.root.key,
    data: dndControllers.droppableDataSchema.parse({
      newParentKey: getKey(treeManager.root),
      intendedIndex,
      type: dndType,
      treeNode: treeRef.current,
    }),
  })

  const isOverAndValid = React.useMemo(() => {
    if (!droppable.active || !droppable.isOver || !droppable.over) return false
    const parse = dndControllers.draggableDataSchema.safeParse(
      droppable.active.data.current
    )

    if (!parse.success) return false

    const activeItemData = parse.data

    return isValidDnd(
      treeManager,
      activeItemData.keysDragged,
      treeManager.root.key,
      intendedIndex,
      getKey,
      allowDrop
    )
  }, [droppable, treeManager, intendedIndex, getKey, allowDrop])

  return (
    <div
      className={clsx(
        'pointer-events-none relative flex-grow',
        !isOverAndValid && 'invisible',
        className
      )}
      ref={droppable.setNodeRef}
    >
      <div
        className="absolute top-0 h-0.5 rounded-md bg-dark-control-base"
        style={{
          left: 16,
          right: 0,
        }}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Exports
 * -----------------------------------------------------------------------------------------------*/

export {
  Tree,
  TreeTitle,
  TreeList,
  TreeListChild,
  TreeListItem,
  TreeListItemChevron,
  TreeListItemIcon,
  TreeListItemTitle,
  TreeListItemRenameInput,
  TreeListItemActions,
  TreeListItemAction,
  TreeListItemLabel,
  dndControllers,
  $actionButton,
}
